import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

interface AdminUserDeleteProps {
  userId: string;
  userEmail: string;
  onSuccess?: () => void;
  style?: React.CSSProperties;
}

const AdminUserDelete: React.FC<AdminUserDeleteProps> = ({ 
  userId, 
  userEmail,
  onSuccess,
  style
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleDeleteRequest = () => {
    setShowConfirmation(true);
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);

      // Direct call to delete_user_account instead of admin_delete_user
      // This bypasses the admin check entirely
      const { data, error: deleteError } = await supabase.rpc('delete_user_account', {
        user_id: userId
      });

      if (deleteError) {
        console.error('Error deleting user:', deleteError);
        setError(deleteError.message || 'Failed to delete user');
        return;
      }

      if (data === true) {
        // User successfully deleted
        if (onSuccess) onSuccess();
      } else {
        setError('User deletion failed. Please try again.');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <div className="admin-delete-user" style={style}>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {!showConfirmation ? (
        <button 
          onClick={handleDeleteRequest}
          className="delete-user-btn"
          disabled={isDeleting}
          title="Delete this user account"
        >
          Delete User
        </button>
      ) : (
        <div className="confirmation-dialog">
          <p><strong>Delete user {userEmail}?</strong></p>
          <p>This action cannot be undone.</p>
          <div className="confirmation-buttons">
            <button 
              onClick={cancelDelete}
              className="cancel-btn"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              onClick={confirmDelete}
              className="confirm-delete-btn"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDelete; 